import { yupResolver } from "@hookform/resolvers/yup";
import defaultAxios, { AxiosError } from "axios";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useHistory } from "react-router-dom";
import Swal from "sweetalert2";
import { captchaSiteKey2 } from "../../../constants/common";
import useRequestResetPassword from "../../../services/mutations/useRequestResetPassword";
import {
    LupaPasswordFormValues,
    lupaPasswordSchema,
} from "../lupaPasswordSchema";

const useLupaPasswordForm = () => {
    const history = useHistory();
    const requestResetPasswordMutation = useRequestResetPassword();
    const [captchaToken, setCaptchaToken] = useState<string | null>(null);

    const form = useForm<LupaPasswordFormValues>({
        resolver: yupResolver(lupaPasswordSchema),
        defaultValues: {
            email: "",
        },
    });

    const queryLoggedIn = new URLSearchParams(window.location.search).get(
        "loggedIn",
    );

    const handleErrorResponse = (error: unknown) => {
        if (!defaultAxios.isAxiosError(error)) return;

        const serverError = error as AxiosError<any>;
        if (!serverError.response) return;

        const fieldError = serverError.response.data;
        if (fieldError?.errors) {
            Object.keys(fieldError.errors).forEach((key) => {
                const errorMessage = fieldError.errors[key];
                form.setError(key as keyof LupaPasswordFormValues, {
                    type: "manual",
                    message: errorMessage[0],
                });
            });
            return;
        }

        Swal.fire({
            title: `${fieldError.message}`,
            position: "top-end",
            showConfirmButton: false,
            icon: "error",
            toast: true,
            timer: 3000,
            timerProgressBar: true,
            showCloseButton: true,
            customClass: {
                container: "my-swal",
            },
        });
    };

    const onSubmit = (values: LupaPasswordFormValues) => {
        if (!captchaToken) {
            Swal.fire({
                title: "Terjadi Kesalahan!",
                text: "Tolong centang box reCAPTCHA",
                icon: "error",
                confirmButtonColor: "#45A779",
                customClass: {
                    container: "my-swal",
                },
            });
            return;
        }

        const formData = new FormData();
        formData.append("email", values.email);

        requestResetPasswordMutation.mutate(formData, {
            onSuccess: (data) => {
                Swal.fire({
                    title: `${data.message}`,
                    position: "top-end",
                    showConfirmButton: false,
                    icon: data.code === 200 ? "success" : "error",
                    toast: true,
                    timer: 3000,
                    timerProgressBar: true,
                    showCloseButton: true,
                    customClass: {
                        container: "my-swal",
                    },
                });
                if (data.code === 200) {
                    history.push("/");
                }
            },
            onError: (error) => {
                handleErrorResponse(error);
                console.error(error);
            },
        });
    };

    return {
        backTo: queryLoggedIn ? "/pengaturan" : "/",
        backToLabel: queryLoggedIn
            ? "Kembali ke Pengaturan"
            : "Kembali ke Login",
        captchaSiteKey: captchaSiteKey2,
        form,
        isButtonLoading: requestResetPasswordMutation.isLoading,
        onCaptchaChange: (value: string | null) => {
            setCaptchaToken(value);
        },
        onCaptchaExpired: () => {
            setCaptchaToken(null);
        },
        onSubmit,
    };
};

export type LupaPasswordFormModel = ReturnType<typeof useLupaPasswordForm>;

export default useLupaPasswordForm;
